import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Layout from "../../components/Layout";
import ResponseMarkdown, { SourcesList } from "../../components/ResponseMarkdown";
import config from "../../config";
import { PendingBox, PendingRow, Spinner } from "./Pending";
import "./PromptDetail.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Deterministic color per model so every run/chart/table uses the same
// color for the same model.
const MODEL_COLORS = [
  "#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444",
  "#06b6d4", "#ec4899", "#84cc16", "#6366f1", "#f97316",
];
function colorFor(model, allModels) {
  const idx = allModels.indexOf(model);
  return MODEL_COLORS[idx % MODEL_COLORS.length];
}

function pct(rate) {
  return `${Math.round((rate || 0) * 100)}%`;
}

export default function PromptDetail() {
  const { reportId, promptNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  // Prompt text arrives instantly via router state from the Prompts tab; on a
  // deep link, the (small) report doc supplies it while the detail computes.
  const [promptText, setPromptText] = useState(location.state?.promptText || "");
  useEffect(() => {
    if (promptText) return;
    fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        const p = (d.report?.prompts || []).find((x) => Number(x.number) === Number(promptNumber));
        if (p) setPromptText(p.text);
      })
      .catch(() => { /* the detail response carries the text too */ });
  }, [reportId, promptNumber, promptText]);
  const [error, setError] = useState("");
  const [metric, setMetric] = useState("mentions"); // 'mentions' | 'links'
  const [expandedRun, setExpandedRun] = useState(null); // runNumber; null = only latest

  useEffect(() => {
    setLoading(true); setError("");
    fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}/prompts/${promptNumber}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setDetail(d.detail);
          // Expand the latest run by default
          const firstRun = d.detail?.responses?.[0]?.runNumber;
          if (firstRun != null) setExpandedRun(firstRun);
        } else {
          setError(d.error || "Failed to load prompt detail.");
        }
      })
      .catch(() => setError("Network error."))
      .finally(() => setLoading(false));
  }, [reportId, promptNumber]);

  // Stable, sorted list of models that ever appeared for this prompt.
  const allModels = useMemo(() => {
    if (!detail) return [];
    const seen = new Set();
    for (const row of detail.timeSeries?.runs || []) {
      for (const m of Object.keys(row.byModel || {})) seen.add(m);
    }
    for (const m of Object.keys(detail.totals?.byModel || {})) seen.add(m);
    return Array.from(seen).sort();
  }, [detail]);

  const chartData = useMemo(() => {
    if (!detail) return null;
    const runs = detail.timeSeries?.runs || [];
    // X axis = run labels (date when available, else runNumber)
    const labels = runs.map((r) =>
      r.completedAt
        ? new Date(r.completedAt).toLocaleDateString()
        : `#${r.runNumber}`,
    );
    const datasets = allModels.map((model) => {
      const data = runs.map((r) => {
        const s = r.byModel?.[model];
        if (!s || !s.count) return null; // null leaves a gap rather than showing 0
        const hits = metric === "mentions" ? s.mentions : s.links;
        return hits / s.count;
      });
      return {
        label: model,
        data,
        borderColor: colorFor(model, allModels),
        backgroundColor: colorFor(model, allModels),
        spanGaps: true,
        tension: 0.25,
      };
    });
    return { labels, datasets };
  }, [detail, allModels, metric]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: 0, max: 1,
        ticks: { callback: (v) => `${Math.round(v * 100)}%` },
      },
    },
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${Math.round((ctx.parsed.y || 0) * 100)}%`,
        },
      },
    },
  }), []);

  if (error || (!loading && !detail)) {
    return (
      <Layout>
        <div className="prompt-detail-page">
          <div className="pd-error">{error || "Prompt not found."}</div>
          <button
            type="button"
            className="pd-back-btn"
            onClick={() => navigate(`/gpt-seo-tracking/reports/${reportId}/prompts`)}
          >
            ← Back to prompts
          </button>
        </div>
      </Layout>
    );
  }

  const pending = !detail;
  const prompt = detail?.prompt || { number: promptNumber, text: promptText };
  const summary = detail?.summary || {};
  const totals = detail?.totals || {};
  const topCompetitors = detail?.topCompetitors || [];
  const topDomains = detail?.topDomains || [];
  const responses = detail?.responses || [];
  const stat = (v) => (pending ? <Spinner size={18} /> : v);

  return (
    <Layout>
      <div className="prompt-detail-page">
        <div className="pd-breadcrumb">
          <button
            type="button"
            className="pd-back-btn"
            onClick={() => navigate(`/gpt-seo-tracking/reports/${reportId}/prompts`)}
          >
            ← All prompts
          </button>
        </div>

        <header className="pd-header">
          <div className="pd-prompt-number">Prompt #{prompt.number}</div>
          <h1 className="pd-prompt-text">{prompt.text || <span className="muted">Loading prompt…</span>}</h1>
        </header>

        <section className="pd-summary">
          <div className="pd-stat">
            <div className="pd-stat-value">{stat(summary.totalRuns)}</div>
            <div className="pd-stat-label">runs</div>
          </div>
          <div className="pd-stat">
            <div className="pd-stat-value">{stat(summary.totalResponses)}</div>
            <div className="pd-stat-label">responses</div>
          </div>
          <div className="pd-stat">
            <div className="pd-stat-value">{stat(pct(summary.overallMentionRate))}</div>
            <div className="pd-stat-label">mention rate</div>
          </div>
          <div className="pd-stat">
            <div className="pd-stat-value">{stat(pct(summary.overallLinkRate))}</div>
            <div className="pd-stat-label">link rate</div>
          </div>
          <div className="pd-stat pd-stat-dates">
            <div className="pd-stat-label">First run</div>
            <div>{summary.firstRunAt ? new Date(summary.firstRunAt).toLocaleDateString() : "—"}</div>
            <div className="pd-stat-label" style={{ marginTop: 6 }}>Last run</div>
            <div>{summary.lastRunAt ? new Date(summary.lastRunAt).toLocaleDateString() : "—"}</div>
          </div>
        </section>

        <section className="pd-chart-section">
          <div className="pd-chart-header">
            <h2>Performance over time</h2>
            <div className="pd-metric-toggle" role="radiogroup" aria-label="Chart metric">
              <button
                type="button"
                role="radio"
                aria-checked={metric === "mentions"}
                className={metric === "mentions" ? "active" : ""}
                onClick={() => setMetric("mentions")}
              >
                Mention rate
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={metric === "links"}
                className={metric === "links" ? "active" : ""}
                onClick={() => setMetric("links")}
              >
                Link rate
              </button>
            </div>
          </div>
          <div className="pd-chart-wrap">
            {pending ? (
              <PendingBox height={260} />
            ) : chartData && chartData.datasets.length > 0 ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <div className="pd-chart-empty">No runs yet for this prompt.</div>
            )}
          </div>
        </section>

        <section className="pd-totals">
          <h2>By model</h2>
          <table className="pd-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Responses</th>
                <th>Mentions</th>
                <th>Mention rate</th>
                <th>Links</th>
                <th>Link rate</th>
              </tr>
            </thead>
            <tbody>
              {pending && <PendingRow cols={6} />}
              {allModels.map((model) => {
                const t = totals.byModel?.[model];
                if (!t) return null;
                return (
                  <tr key={model}>
                    <td>
                      <span className="pd-model-swatch" style={{ background: colorFor(model, allModels) }} />
                      {model}
                    </td>
                    <td>{t.total}</td>
                    <td>{t.mentions}</td>
                    <td>{pct(t.mentionRate)}</td>
                    <td>{t.links}</td>
                    <td>{pct(t.linkRate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <div className="pd-two-col">
          <section className="pd-top-list">
            <h2>Top competitors</h2>
            {pending ? <Spinner size={20} /> : topCompetitors.length === 0 ? (
              <p className="pd-empty">No competitors detected in responses to this prompt.</p>
            ) : (
              <ol className="pd-ranked-list">
                {topCompetitors.map((c) => (
                  <li key={c.name}>
                    <span className="pd-rank-name">{c.name}</span>
                    <span className="pd-rank-count">{c.count}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className="pd-top-list">
            <h2>Top sources</h2>
            {pending ? <Spinner size={20} /> : topDomains.length === 0 ? (
              <p className="pd-empty">No URLs cited in responses to this prompt.</p>
            ) : (
              <ol className="pd-ranked-list">
                {topDomains.map((d) => (
                  <li key={d.domain}>
                    <span className="pd-rank-name">
                      {d.domain}
                      {d.isClient && <span className="pd-client-badge">client</span>}
                    </span>
                    <span className="pd-rank-count">{d.count}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <section className="pd-responses">
          <h2>Responses</h2>
          {pending ? <Spinner size={20} /> : responses.length === 0 ? (
            <p className="pd-empty">No responses recorded yet.</p>
          ) : (
            responses.map((group) => {
              const isOpen = expandedRun === group.runNumber;
              const runModels = Object.keys(group.byModel || {}).sort();
              return (
                <div key={group.runNumber} className={`pd-run-group ${isOpen ? "open" : ""}`}>
                  <button
                    type="button"
                    className="pd-run-header"
                    onClick={() => setExpandedRun(isOpen ? null : group.runNumber)}
                    aria-expanded={isOpen}
                  >
                    <span className="pd-run-title">
                      Run #{group.runNumber}
                      {group.completedAt && (
                        <span className="pd-run-date"> · {new Date(group.completedAt).toLocaleString()}</span>
                      )}
                    </span>
                    <span className="pd-run-caret">{isOpen ? "▾" : "▸"}</span>
                  </button>
                  {isOpen && (
                    <div className="pd-run-body">
                      {runModels.map((model) => (
                        <div key={model} className="pd-model-block">
                          <div className="pd-model-header">
                            <span className="pd-model-swatch" style={{ background: colorFor(model, allModels) }} />
                            <span className="pd-model-name">{model}</span>
                            <span className="pd-model-meta">
                              {group.byModel[model].length} responses
                            </span>
                          </div>
                          <ul className="pd-response-list">
                            {group.byModel[model].map((resp, idx) => (
                              <li key={idx} className={`pd-response ${resp.clientMentioned ? "mentioned" : ""}`}>
                                <div className="pd-response-tags">
                                  {resp.clientMentioned && <span className="pd-tag pd-tag-hit">mentioned</span>}
                                  {resp.clientLinked && <span className="pd-tag pd-tag-link">linked</span>}
                                  {resp.city && (
                                    <span className="pd-tag pd-tag-city">
                                      {resp.city}, {resp.cityStateAbbr || ""}
                                    </span>
                                  )}
                                </div>
                                <ResponseMarkdown text={resp.response} className="pd-response-text" citations={resp.citations} />
                                <SourcesList citations={resp.citations} urls={resp.urlsReferenced} text={resp.response} />
                                {resp.competitorsMentioned?.length > 0 && (
                                  <div className="pd-response-meta">
                                    Competitors: {resp.competitorsMentioned.join(", ")}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </section>
      </div>
    </Layout>
  );
}
